import { MASKED_TEXT_SELECTOR, screenFor } from "./screens";

/**
 * What PostHog is never allowed to receive, enforced on the way out.
 *
 * URLs are the leak that masking cannot see: the sign-in callback's URL
 * carries the sign-in secret, and `/friends/<id>` carries another person's id.
 * Every URL PostHog would store is cut down to our route pattern (or, for
 * another site, to its origin) before the event leaves the browser. Campaign
 * parameters (utm_*, gclid) survive, because PostHog reads them into their own
 * properties before this runs.
 */
/**
 * Any property named like a URL, a path or a referrer. PostHog builds many of
 * these by prefixing (`$initial_current_url`, `$session_entry_url`,
 * `$prev_pageview_pathname`), so they are matched by name, not listed.
 */
const URL_PROPERTY = /(?:^|_)(?:url|pathname|referrer|href)$/i;

export function scrubUrl(raw: unknown, origin: string): unknown {
  if (typeof raw !== "string" || raw === "" || raw === "$direct") return raw;
  try {
    const url = new URL(raw, origin);
    if (url.origin !== origin) return url.origin;
    const route = screenFor(url.pathname).route;
    // `$pathname` and friends hold a path; the rest hold a full URL.
    return raw.startsWith("/") ? route : `${origin}${route}`;
  } catch {
    return undefined;
  }
}

type Properties = Record<string, unknown>;

function scrubProperties(properties: Properties | undefined, origin: string): Properties | undefined {
  if (!properties) return properties;
  const next: Properties = { ...properties };
  for (const [key, value] of Object.entries(next)) {
    if (URL_PROPERTY.test(key.replace(/^\$/, ""))) next[key] = scrubUrl(value, origin);
  }
  // Person properties can also ride inside the event's own properties.
  for (const key of ["$set", "$set_once"] as const) {
    const nested = next[key];
    if (nested && typeof nested === "object" && !Array.isArray(nested)) next[key] = scrubProperties(nested as Properties, origin);
  }
  const heatmap = next.$heatmap_data;
  if (heatmap && typeof heatmap === "object") {
    // Heatmap clicks are keyed by the page URL they happened on.
    const merged: Record<string, unknown[]> = {};
    for (const [url, points] of Object.entries(heatmap as Record<string, unknown>)) {
      const key = String(scrubUrl(url, origin));
      merged[key] = [...(merged[key] ?? []), ...(Array.isArray(points) ? points : [])];
    }
    next.$heatmap_data = merged;
  }
  return next;
}

/** rrweb's event types that carry the page URL in the clear: Meta (4) and Custom (5). */
const META_EVENT = 4;
const CUSTOM_EVENT = 5;

/**
 * Replay batches carry the page URL inside rrweb's own events. The recorder
 * already passes them through `maskReplayUrl`; this catches any it missed.
 */
function scrubSnapshots(data: unknown, origin: string): unknown {
  if (!Array.isArray(data)) return data;
  return data.map((entry: unknown) => {
    if (!entry || typeof entry !== "object") return entry;
    const event = entry as { type?: unknown; data?: Record<string, unknown> };
    if (event.type === META_EVENT && event.data && "href" in event.data) {
      return { ...event, data: { ...event.data, href: scrubUrl(event.data.href, origin) } };
    }
    const payload = event.data?.payload;
    if (event.type === CUSTOM_EVENT && payload && typeof payload === "object" && "href" in payload) {
      const href = scrubUrl((payload as { href: unknown }).href, origin);
      return { ...event, data: { ...event.data, payload: { ...payload, href } } };
    }
    return entry;
  });
}

export interface OutgoingEvent {
  properties?: Properties;
  $set?: Properties;
  $set_once?: Properties;
}

/** PostHog's `before_send`: every event, every time, whoever captured it. */
export function scrubEvent<T extends OutgoingEvent>(event: T | null, origin: string): T | null {
  if (!event) return event;
  const scrubbed = scrubProperties(event.properties, origin);
  const properties =
    scrubbed && "$snapshot_data" in scrubbed ? { ...scrubbed, $snapshot_data: scrubSnapshots(scrubbed.$snapshot_data, origin) } : scrubbed;
  return {
    ...event,
    properties,
    $set: scrubProperties(event.$set, origin),
    $set_once: scrubProperties(event.$set_once, origin),
  };
}

/**
 * The recorder's one hook for URLs, which PostHog uses for two things.
 * Called with only a `name`, it is masking a page URL for the replay
 * (the page's address, URL changes, page views): that is cut down to the
 * route pattern like every other URL. Called with a whole request, it is
 * network capture, which a project setting in PostHog can switch on: every
 * such request is dropped, so the replay never holds our API traffic.
 */
export function maskReplayRequest(request: Readonly<Record<string, unknown>>, origin: string): { name: string } | null {
  const keys = Object.keys(request);
  if (keys.length === 1 && keys[0] === "name" && typeof request.name === "string") {
    const url = scrubUrl(request.name, origin);
    return typeof url === "string" ? { name: url } : null;
  }
  return null;
}

/**
 * Attributes that can carry a name, an id or typed text. On a masked screen
 * their values are replaced in the replay, as the words are.
 */
const PERSONAL_ATTRIBUTES = new Set(["aria-label", "aria-description", "title", "alt", "placeholder", "href", "value", "content", "src"]);

export function maskAttribute(name: string, value: string, element?: Element): string {
  if (!PERSONAL_ATTRIBUTES.has(name.toLowerCase())) return value;
  // The head holds our stylesheets and icons, never anything personal, and
  // masking a stylesheet's href would leave the replay unstyled.
  if (element && !element.closest("body")) return value;
  // With no element to place it, the value is treated as on a masked screen.
  try {
    return !element || element.closest(MASKED_TEXT_SELECTOR) ? "*".repeat(value.length) : value;
  } catch {
    return "*".repeat(value.length);
  }
}
